import { useState } from "react"

export type DraftParse<T> =
	| { ok: true; value: T }
	/** No message means the text cannot be fixed, so the field goes back to the stored value. */
	| { ok: false; error?: string }

interface Options<T> {
	onCommit: (value: T) => void
	parse: (text: string) => DraftParse<T>
	/** Text for a stored value. Defaults to the value itself, blank for nullish. */
	format?: (value: T) => string
}

/**
 * A text field the user edits freely and that saves on blur. A write from outside
 * wins, so a kind switch or another tab's edit reseeds the text without a remount.
 */
export function useCommittedDraft<T>(
	external: T,
	{ onCommit, parse, format = (value) => String(value ?? "") }: Options<T>,
) {
	const incoming = format(external)
	const [draft, setDraft] = useState(() => ({ text: incoming, seen: incoming }))
	const [error, setError] = useState<string | null>(null)

	if (incoming !== draft.seen) {
		setDraft({ text: incoming, seen: incoming })
		setError(null)
	}

	return {
		value: draft.text,
		error,
		setValue: (next: string) => {
			setDraft((current) => ({ ...current, text: next }))
			setError(null)
		},
		commit: () => {
			const parsed = parse(draft.text)
			if (parsed.ok) {
				setError(null)
				onCommit(parsed.value)
				return
			}
			if (parsed.error) setError(parsed.error)
			else setDraft({ text: incoming, seen: incoming })
		},
	}
}
